import crypto from 'crypto'

import { serialize } from 'cookie'
import { GetServerSidePropsContext } from 'next'

import { CONFIG_BUILD_ENV } from '@src/config/env'
import { SERVER_CUSTOM_EXPERIMENTS } from '@src/config/server-experiments'
import { isPreviewEnv } from '@src/utils/general.utils'
import { generateNonce } from '@src/utils/nonceGenerator'

import getStaticWebScripts from '../../scripts/get-static-web-scripts.json'

import { SlugInfo } from './api/fetch-slug-info'
import { CACHE_CONTROL_200, CACHE_CONTROL_200_NO_CACHE, CACHE_CONTROL_4XX_5XX } from './server-side.constant'

// Type definition for web modules scripts
interface StaticWebScripts {
  name: string
  hash: string
  script: string
}

export interface Options {
  shouldCache: boolean
  deviceType: string
  contentVersion?: string | null
  pathName: string
  status?: number
  locale?: string
  slugInfo?: SlugInfo | null
}

export function getCookieFromHeaderCookie(ctx: GetServerSidePropsContext, headerKey: string): string | null {
  const headerValue = getRequestHeaders(ctx, headerKey)
  if (!headerValue) {
    return null
  }

  const [cookieKey, cookieValue] = headerValue.split('__')
  if (!cookieKey || !cookieValue) {
    return null
  }

  const cookie = getCookieHeaderValue(cookieKey, cookieValue, { isSessionCookie: cookieValue.endsWith('_forced') })
  return cookie
}

export function getExperimentAdjustedRequestPath(ctx: GetServerSidePropsContext, reqPath: string): string {
  const pageRewrite = getRequestHeaders(ctx, 'xq-page-ab-rewrite')
  const pageAbCookie = getCookieFromHeaderCookie(ctx, 'xq-page-ab-cookie')

  if (!pageAbCookie || !pageRewrite) {
    return reqPath
  }
  ctx.res.setHeader('xq-page-ab-rewrite', pageRewrite)
  ctx.res.setHeader('xq-page-ab-cookie', pageAbCookie)

  const entries = Object.entries(SERVER_CUSTOM_EXPERIMENTS)
  const experiment = entries.find(([_, value]) => value.has(reqPath))
  if (!experiment?.[1].has(pageRewrite)) {
    return reqPath
  }

  setCookie(ctx, pageAbCookie)
  return pageRewrite
}

function isActual404(slugInfo: SlugInfo | null | undefined) {
  if (!slugInfo) return true
  if ('slug' in slugInfo && slugInfo.slug !== '/404') return false
  return true
}

export function setCommonResponseHeaders(ctx: GetServerSidePropsContext, options: Options) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const host = ctx.req.headers.host! || 'NA'
  // @i18n-ignore
  const cacheControlKey = 'Cache-Control'
  if (options.status && options.status >= 400 && options.status < 599) {
    if (isActual404(options.slugInfo)) {
      ctx.res.setHeader('Cache-Control', CACHE_CONTROL_4XX_5XX)
    } else {
      ctx.res.removeHeader('Set-Cookie')
      ctx.res.setHeader(cacheControlKey, CACHE_CONTROL_200_NO_CACHE)
    }
  } else if (options.shouldCache && !isPreviewEnv(host)) {
    ctx.res.setHeader(cacheControlKey, CACHE_CONTROL_200)
  } else {
    ctx.res.setHeader(cacheControlKey, CACHE_CONTROL_200_NO_CACHE)
  }

  // ctx.res.setHeader('xq-req-host', host)
  ctx.res.setHeader('xq-device-type', options.deviceType)
  ctx.res.setHeader('xq-content-version', options.contentVersion ?? 'latest')
  if (process.env.NEXT_PUBLIC_RELEASE_VERSION) {
    ctx.res.setHeader('xq-build-version', process.env.NEXT_PUBLIC_RELEASE_VERSION)
  }
  // Add locale headers for Datadog APM tracking
  if (options.locale) {
    ctx.res.setHeader('xq-locale', options.locale)
  }
  // ctx.res.setHeader('xq-req-ssr-uri', host + options.pathName)
  if (host !== 'www.quince.com') {
    ctx.res.setHeader('X-Robots-Tag', 'noindex')
  }
  setCSPHeader(ctx, host)
}

export function getCookieHeaderValue(
  cookieKey: string,
  cookieValue: string,
  options: {
    isSessionCookie?: boolean
    expiryMinutes?: number
    isSecure?: boolean
  },
) {
  const expiryMinutes = options.expiryMinutes ?? 400 * 24 * 60
  return serialize(cookieKey, cookieValue, {
    path: '/',
    expires: options.isSessionCookie ? undefined : new Date(Date.now() + expiryMinutes * 60 * 1000),
    secure: options.isSecure,
    sameSite: 'lax',
  })
}

export function getCookieExpiryDate(expiryMinutes: number): string {
  const date = new Date()
  date.setTime(date.getTime() + expiryMinutes * 60 * 1000)
  return date.toUTCString()
}

export function getRequestHeaders(ctx: GetServerSidePropsContext, headerKey: string): string | undefined {
  const headerValue = ctx.req.headers[headerKey]
  if (Array.isArray(headerValue)) {
    return headerValue[0]
  }
  return headerValue
}

export function setCookie(ctx: GetServerSidePropsContext, value: string) {
  const setCookieHeaderValue = ctx.res.getHeader('set-cookie')
  if (Array.isArray(setCookieHeaderValue)) {
    ctx.res.setHeader('Set-Cookie', [...setCookieHeaderValue, value])
  } else if (setCookieHeaderValue) {
    ctx.res.setHeader('Set-Cookie', [`${setCookieHeaderValue}`, value])
  } else {
    ctx.res.setHeader('Set-Cookie', value)
  }
}

function cspHash(code: string) {
  const digest = crypto.createHash('sha256').update(code, 'utf8').digest('base64')
  return `sha256-${digest}`
}

function setCSPHeader(ctx: GetServerSidePropsContext, _host: string) {
  const nonce = generateNonce()
  ctx.res.setHeader('x-nonce', nonce)
  const hashes = (getStaticWebScripts as StaticWebScripts[]).map(s => `'${cspHash(s.script)}'`).join(' ')
  const cspPolicy = `default-src 'self'; script-src 'report-sample' 'self' 'nonce-${nonce}' 'unsafe-inline' 'unsafe-eval' ${hashes} 'sha256-uYbnit/TAKDP1ZgXJ7nJ6afBudSLQNV93RFSAzTh+d8=' 'sha256-rZOpB50p0BuV/h134+wMyi021XMR1o7W9badYd5586k=' 'sha256-tvbM3K369YeXm5Lbnt4BpIunkyQTsUatRZlu2OE/WzA=' 'sha256-QGbtWOqPiU5MjESK9XzARFB+nAJ+Jx1HBjxgge9e8nE=' 'sha256-9A2VmgajLciB3nKCLND+I1ATKTHFEiVBMWRYtFWMwbw=' 'sha256-Tui1REoFcRjWJYOOEGCwPK2CXK/N8pCpPOHAohoCoiQ=' 'sha256-ZHhzuJVLE6SMT3RvciNWfckQiWMxcdpZqgZOhn2gCuk=' 'sha256-uYbnit/TAKDP1ZgXJ7nJ6afBudSLQNV93RFSAzTh+d8=' 'sha256-rZOpB50p0BuV/h134+wMyi021XMR1o7W9badYd5586k=' 'sha256-20rcpQYZJG6XPRd8SmyUIRkQi7QrBUpFAOJNliZLrUk=' 'sha256-yYwfgy2l39pI3JL97Y9s9tT+EO+epUnmX/eEZC4XTvU=' 'sha256-IIBNyPJCWc+5LWhjBnE/kfHKD4zM54dst646hNwQ3Zw=' 'sha256-BNpzWkLJJ/oEI87Ig4sjmy+rBlBMEKbZgjBWnTT+GaM=' 'sha256-jGJY4NNrjBzo1PkGbspheaDcbxBkZwmccCqKmPsHxTk=' 'sha256-gaW+bUyi6HIn1agK+5SX2xAU9ph1whoEKqeSmskABTw=' https://apps.rokt.com/integrations/referral-tag/referral-tag.js https://bat.bing.com/bat.js https://c.albss.com/p/l/loader.iife.js https://cdn-widgetsrepository.yotpo.com https://cdn.attn.tv https://cdn.gladly.com/assets/chat-sdk/vendor-node_modules_reduxjs_toolkit_dist_redux-toolkit_cjs_production_min_js-node_modules_cha-6a7f77~15457aaa23e3fb693e70.js https://cdn.gladly.com/assets/chat-sdk/vendor-node_modules_reduxjs_toolkit_dist_redux-toolkit_cjs_production_min_js-node_modules_cha-6a7f77~c094d6e00be849ffe9e4.js https://cdn.gladly.com/assets/chat-sdk/apiBootstrap~0193b9c80ec7403c677f.js https://cdn.gladly.qa https://cdn.gladly.qa/assets/chat-sdk/apiBootstrap~5d30de031a9abc9fe3c8.js https://cdn.gladly.qa/assets/chat-sdk/apiBootstrap~0193b9c80ec7403c677f.js https://cdn.gladly.qa/gladly/chat-sdk/widget.js https://cdn.gladly.com/chat-sdk/widget.js https://cdn.pbbl.co/r/2772.js https://cdnjs.cloudflare.com/ajax/libs/snowplow/2.16.3/sp.min.js https://consent.trustarc.com https://d2u34kd1vibov4.cloudfront.net/880f2076.js https://googleads.g.doubleclick.net/pagead/viewthroughconversion/773421270/ https://mm-uxrv.com/js/mm_bd77e9e4-2b0e-425f-a9e5-a68583c5fa2f-68431059.js https://na-library.klarnaservices.com/v1/1.0.27/sdk.js https://osm.klarnaservices.com/lib.js https://pncqz.quince.com/load https://qnc-cdn.staging.truefitcorp.com/fitrec/en-us-latest/js/tf-integration.js https://res4.applovin.com/p/104/b/bs.c9e1074f5b3f9fc8ea15d152add07294-1.iife.js https://s.axon.ai/pixel.js https://s3.amazonaws.com/beehiiv-adnetwork-production/pixel-v2.js https://www.clarity.ms/s/0.8.1/clarity.js https://www.google-analytics.com/analytics.js https://www.googletagmanager.com/gtag/js https://www.redditstatic.com/ads/pixel.js; style-src 'report-sample' 'self' 'unsafe-inline' https://x.klarnacdn.net; object-src 'none'; base-uri 'self'; connect-src 'self' https://a.clarity.ms https://analytics.google.com https://api-integ.onequince.com https://api-pre-integ.onequince.com https://api-staging.onequince.com https://api-js.mixpanel.com https://api-prod-public.onequince.com https://api-qa-public.onequince.com https://api-qa.onequince.com https://api.onequince.com https://assetsconfigcdn.org https://b.applovin.com https://b.clarity.ms https://bat.bing.com https://beyondwickedmapping.org https://cdn.attn.tv https://cdn.gladly.com https://cdn.gladly.qa https://cdn.gladly.qa/ https://cdn.gladly.qa/assets/chat-sdk/apiBootstrap~0193b9c80ec7403c677f.js https://checkout-integ.onequince.com https://cloudflare-dns.com https://cognito-identity.ap-south-1.amazonaws.com https://cognito-idp.ap-south-1.amazonaws.com https://consent.trustarc.com https://e.clarity.ms https://esh3y4pxhj.execute-api.ap-south-1.amazonaws.com https://eu.klarnaevt.com https://events.attentivemobile.com https://f.clarity.ms https://featureassets.org https://fievnbyjld.execute-api.us-west-1.amazonaws.com https://google.com https://i.clarity.ms https://ia.quince.com https://ingestion.apiary.beehiiv.net https://insights.algolia.io https://js.klarna.com https://k.clarity.ms https://k81d2wqh9l-1.algolianet.com https://k81d2wqh9l-2.algolianet.com https://k81d2wqh9l-3.algolianet.com https://k81d2wqh9l-dsn.algolia.net https://labg3uadr2.execute-api.us-west-1.amazonaws.com https://n.clarity.ms https://na-library.klarnaservices.com https://na.klarnaevt.com https://pixel-config.reddit.com https://pncqz.quince.com/ https://portal.brandlock.io https://prodregistryv2.org https://quince-qa.auth.ap-south-1.amazoncognito.com https://quince.attn.tv https://quince-us.attn.tv https://quincedev.attn.tv https://quincedev-ca.attn.tv https://quincedev-us.attn.tv https://re.applovin.com https://s.clarity.ms https://stats.g.doubleclick.net https://storefront-api.onequince.com https://storefront-integ-apigateway.onequince.com https://u9nc5j0mad.execute-api.ap-south-1.amazonaws.com https://www.google-analytics.com https://www.google.co.in https://www.google.com https://www.googletagmanager.com https://www.googletagmanager.com/gtm.js https://www.redditstatic.com https://x.clarity.ms; font-src 'self' data: https://consent.trustarc.com https://fonts.gstatic.com https://x.klarnacdn.net; frame-ancestors 'none'; frame-src 'self' https://cdn.pbbl.co/ https://d.agkn.com/ https://ds-prod-ga-1-tf-ds-c503f2f8.truefitcorp.com/ https://ia.quince.com/ https://td.doubleclick.net/; img-src 'self' http://consent.trustarc.com http://images.quince.com https://alb.reddit.com https://api-integ.onequince.com https://bat.bing.com https://c.clarity.ms https://consent.trustarc.com https://ik.imagekit.io/ https://images.ctfassets.net https://images.quince.com https://portal.brandlock.io https://px0.pbbl.co https://review-images.onequince.com https://www.google-analytics.com https://www.google.ca https://www.google.co.in https://www.google.com https://www.googletagmanager.com; manifest-src 'self'; media-src 'self' blob: data: https://cdn.channelize.io https://cdn.quince.com https://cdn.stamped.io https://images.ctfassets.net https://videos.ctfassets.net https://images.quince.com https://t6q7c3m8.stackpathcdn.com; report-uri https://browser-intake-datadoghq.com/api/v2/logs?dd-api-key=pubbdfcba1d6885fbfa30b91f0a0d1fe230&dd-evp-origin=content-security-policy&ddsource=csp-report&ddtags=service%3ACSP%2Cenv%3A${CONFIG_BUILD_ENV}; worker-src 'self' blob:;`
  ctx.res.setHeader('Content-Security-Policy-Report-Only', cspPolicy)
}
